/**
 * ProductPhotoGallery — upload multiple photos to a product, reorder by drag,
 * click to zoom, delete individual images, non-destructive CROP with a preset
 * ratio picker + reset-to-original. First image = hero.
 *
 * Wired to admin endpoints:
 *   POST   /api/admin/products/{slug}/image           (new upload)
 *   POST   /api/admin/products/{slug}/image/replace   (cropped variant + preserves original)
 *   POST   /api/admin/products/{slug}/image/reset-crop
 */
import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { UploadCloud, X, Star, ImageIcon, Loader2, ZoomIn, Crop, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ImageZoomLightbox } from "./ImageZoomLightbox";
import { ImageCropModal } from "./ImageCropModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:") || u.startsWith("blob:")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

export const ProductPhotoGallery = ({ slug, images = [], imagesOriginals = [], token, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [cropState, setCropState] = useState(null); // { index, url }
  const [confirmResetIndex, setConfirmResetIndex] = useState(null);
  const inputRef = useRef(null);

  const originalAt = (i) => imagesOriginals?.[i] || "";
  const hasOriginal = (i) => Boolean(originalAt(i));

  const upload = async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    setUploading(true);
    try {
      let latest = images;
      let latestOriginals = imagesOriginals || [];
      for (const file of arr) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API}/admin/products/${slug}/image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.text();
          throw new Error(err || "Upload failed");
        }
        const data = await res.json();
        latest = data.images || latest;
      }
      onChange?.(latest, latestOriginals);
      toast.success(`${arr.length} photo${arr.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = async (i) => {
    const next = images.filter((_, idx) => idx !== i);
    const nextOriginals = (imagesOriginals || []).filter((_, idx) => idx !== i);
    const res = await fetch(`${API}/admin/products/${slug}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ images: next, images_originals: nextOriginals }),
    });
    if (!res.ok) return toast.error("Could not remove photo");
    onChange?.(next, nextOriginals);
    toast.success("Photo removed");
  };

  const reorder = async (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...images];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    const nextOriginals = [...(imagesOriginals || [])];
    while (nextOriginals.length < images.length) nextOriginals.push("");
    const [origItem] = nextOriginals.splice(from, 1);
    nextOriginals.splice(to, 0, origItem);
    // Optimistic UI
    onChange?.(next, nextOriginals);
    const res = await fetch(`${API}/admin/products/${slug}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ images: next, images_originals: nextOriginals }),
    });
    if (!res.ok) toast.error("Could not save order");
  };

  const setHero = (i) => reorder(i, 0);

  const openCropAt = (i) => {
    // Always crop from the ORIGINAL when one is stored so re-crops never
    // degrade further. Otherwise crop from the current image.
    const url = resolveUrl(originalAt(i) || images[i]);
    setCropState({ index: i, url });
  };

  const saveCrop = async (blob, meta) => {
    const { index } = cropState || {};
    if (index == null) return;
    const fd = new FormData();
    fd.append("index", String(index));
    fd.append("file", new File([blob], `cropped-${index}.webp`, { type: "image/webp" }));
    try {
      const res = await fetch(`${API}/admin/products/${slug}/image/replace`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || "Crop save failed");
      }
      const data = await res.json();
      onChange?.(data.images || images, data.images_originals || imagesOriginals);
      toast.success(`Crop saved · ${meta?.width}×${meta?.height} WebP`);
      setCropState(null);
    } catch (e) {
      toast.error(e.message || "Crop save failed");
    }
  };

  const resetCrop = async (index) => {
    setConfirmResetIndex(null);
    try {
      const res = await fetch(`${API}/admin/products/${slug}/image/reset-crop`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ index }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || "Reset failed");
      }
      const data = await res.json();
      onChange?.(data.images || images, data.images_originals || imagesOriginals);
      toast.success("Reverted to original");
    } catch (e) {
      toast.error(e.message || "Reset failed");
    }
  };

  return (
    <div className="col-span-12" data-testid="product-photo-gallery">
      <div className="overline text-forest/60 mb-2 flex items-center justify-between">
        <span>Product photos {images.length > 0 && <span className="text-forest/40">· {images.length}</span>}</span>
        <span className="text-forest/40 text-[10px] normal-case">Drag to reorder · first = hero · tap/hover for actions</span>
      </div>

      {images.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
          {images.map((src, i) => (
            <motion.div
              key={`${src}-${i}`}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { reorder(dragIndex, i); setDragIndex(null); }}
              layout
              className={`relative group aspect-square bg-beige overflow-hidden rounded-md border ${dragIndex === i ? "border-forest ring-2 ring-champagne" : "border-hairline"} ${i === 0 ? "ring-1 ring-gold" : ""}`}
              data-testid={`gallery-photo-${i}`}
            >
              <img
                src={resolveUrl(src)}
                alt={`Photo ${i + 1}`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover cursor-zoom-in"
                onClick={() => setZoom({ images: images.map(resolveUrl), index: i })}
              />
              {i === 0 && (
                <div className="absolute top-1 left-1 bg-gold text-white overline text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1" data-testid={`hero-badge-${i}`}>
                  <Star size={9} strokeWidth={2} fill="currentColor" /> Hero
                </div>
              )}
              {hasOriginal(i) && (
                <div
                  className={`absolute top-1 bg-black/70 text-white overline text-[8px] px-1.5 py-0.5 rounded z-[1] ${i === 0 ? "left-[46px]" : "left-1"}`}
                  data-testid={`cropped-badge-${i}`}
                >
                  Cropped
                </div>
              )}
              {/* Actions:
                  - Mobile/tablet (<lg): always-visible slim bottom bar with translucent bg.
                  - Desktop (≥lg): fades in on hover with a dark overlay. */}
              <div className="absolute inset-x-0 bottom-0 lg:inset-0 flex items-end justify-between p-1.5 gap-1 bg-gradient-to-t from-black/70 to-black/0 lg:bg-transparent lg:group-hover:bg-black/40 transition-colors duration-200 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-within:opacity-100">
                <div className="flex gap-1 flex-wrap">
                  {i !== 0 && (
                    <button
                      type="button"
                      onClick={() => setHero(i)}
                      title="Set as hero"
                      data-testid={`set-hero-${i}`}
                      className="p-1 rounded-full bg-white text-forest hover:bg-champagne"
                    >
                      <Star size={11} strokeWidth={1.8} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openCropAt(i)}
                    title="Crop"
                    data-testid={`crop-photo-${i}`}
                    className="p-1 rounded-full bg-white text-forest hover:bg-gold hover:text-white"
                  >
                    <Crop size={11} strokeWidth={1.8} />
                  </button>
                  {hasOriginal(i) && (
                    <button
                      type="button"
                      onClick={() => setConfirmResetIndex(i)}
                      title="Reset to original"
                      data-testid={`reset-crop-${i}`}
                      className="p-1 rounded-full bg-white text-forest hover:bg-champagne"
                    >
                      <RotateCcw size={11} strokeWidth={1.8} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setZoom({ images: images.map(resolveUrl), index: i })}
                    title="Zoom"
                    data-testid={`zoom-photo-${i}`}
                    className="p-1 rounded-full bg-white text-forest hover:bg-champagne"
                  >
                    <ZoomIn size={11} strokeWidth={1.8} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  title="Delete"
                  data-testid={`delete-photo-${i}`}
                  className="p-1 rounded-full bg-white text-forest hover:bg-red-500 hover:text-white"
                >
                  <X size={11} strokeWidth={1.8} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-hairline rounded-md aspect-[4/1] flex items-center justify-center text-forest/40 text-sm mb-3" data-testid="gallery-empty">
          <ImageIcon size={16} strokeWidth={1.4} className="mr-2" /> No photos yet
        </div>
      )}

      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        className="flex items-center justify-center gap-2 border border-dashed border-hairline rounded-md py-4 cursor-pointer hover:border-forest/60 transition-colors"
        data-testid="upload-photos-dropzone"
      >
        {uploading ? (
          <>
            <Loader2 size={14} strokeWidth={1.6} className="animate-spin" />
            <span className="text-forest text-sm">Uploading…</span>
          </>
        ) : (
          <>
            <UploadCloud size={16} strokeWidth={1.4} className="text-forest/60" />
            <span className="text-forest text-sm">Drop photos here or click to select — multiple allowed</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          hidden
          onChange={(e) => upload(e.target.files)}
          data-testid="upload-photos-input"
        />
      </label>

      <ImageZoomLightbox state={zoom} onClose={() => setZoom(null)} />

      {cropState && (
        <ImageCropModal
          open
          imageUrl={cropState.url}
          initialAspectKey="4:5"
          onCancel={() => setCropState(null)}
          onSave={saveCrop}
        />
      )}

      {confirmResetIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="reset-crop-confirm"
          className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmResetIndex(null); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5">
            <div className="overline text-forest tracking-[3px] text-[11px] mb-2">Reset to original?</div>
            <p className="text-forest/70 text-sm leading-relaxed mb-4">
              This will replace the cropped image with the original upload. The crop can be re-applied any time.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmResetIndex(null)}
                data-testid="reset-crop-cancel"
                className="overline text-forest border border-hairline rounded-full px-4 py-2 text-[10px] tracking-[2px] hover:bg-beige"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => resetCrop(confirmResetIndex)}
                data-testid="reset-crop-confirm-btn"
                className="overline bg-forest text-white rounded-full px-4 py-2 text-[10px] tracking-[2px] hover:bg-black"
              >
                Reset image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPhotoGallery;
